import type { DisbursementType, Stage } from "./CaseConfiguration.ts";

export enum ConditionType {
  minimumCompensationRecovered = "minimumCompensationRecovered",
  minimumCompensationAwarded = "minimumCompensationAwarded",
  maximumCompensationToPay = "maximumCompensationToPay",
}

export function isConditionType(value: string): value is ConditionType {
  return Object.values(ConditionType).includes(value as ConditionType);
}

export type SuccessFee = {
  conditionType: ConditionType;
  // stepped fees
  step: SuccessStep;
};

export type SuccessStep = {
  triggerAmount: number;
  // success fee as percentage of the base fees at risk: the part of them
  // that the fee due without a success (lowFee) leaves unpaid
  successFeePercentage: number;
  // stepped fees
  next?: SuccessStep;
};

/**
 * The lowest minimum a step can ask to be recovered or awarded. A lost case
 * recovers and is awarded nothing, so a minimum of 0 would make every outcome
 * a success. An amount to pay has no such floor: to pay nothing is the best
 * outcome there is.
 */
export const LOWEST_MINIMUM_COMPENSATION = 1;

/**
 * The trigger amount of a step as outcomes are measured against it: a
 * minimum stored below the lowest one counts as the lowest.
 */
export function triggerAmountOf(
  { triggerAmount }: SuccessStep,
  conditionType: ConditionType,
): number {
  return conditionType === ConditionType.maximumCompensationToPay
    ? triggerAmount
    : Math.max(LOWEST_MINIMUM_COMPENSATION, triggerAmount);
}

/**
 * The success fee percentage a trigger value reaches: the highest among the
 * steps whose trigger amount it meets, wherever they sit in the chain.
 * Undefined when it meets none of them — the success condition is not met.
 */
export function getSuccessFeePercentage(
  caseValue: number,
  { conditionType, step }: SuccessFee,
): number | undefined {
  let percentage: number | undefined;
  // every step is checked: a later one may be met where an earlier one is not
  for (
    let current: SuccessStep | undefined = step;
    current;
    current = current.next
  ) {
    if (triggerAmountOf(current, conditionType) > caseValue) continue;
    percentage = Math.max(percentage ?? 0, current.successFeePercentage);
  }
  return percentage;
}

export type PartyAgreements = {
  partyId: string;
  // CFA between party and their law firm
  lawFirmCfa?: ConditionalFeeAgreement & {
    // typically CFAs cover law firm fees only, but some disbursements could be covered as well (e.g. barrister fees)
    includedDisbursements: DisbursementType[];
  };
  // barrister may be on separate CFA, either contracted by party or their law firm
  barristerCfa?: ConditionalFeeAgreement & {
    engagedBy: "lawFirm" | "party";
  };
  // TPF
  thirdPartyFunding?: {
    active: boolean;
  };
  // ATE
  afterTheEventInsurance?: {
    active: boolean;
  };
};

/**
 * Does the barrister CFA apply? It must be active and not mooted. The moot:
 * a PARTY-engaged (or legacy) barrister CFA conflicts with the law-firm CFA
 * explicitly covering barrister fees (`includedDisbursements:
 * ["barristerFee"]`) — the law-firm CFA's coverage wins outright: the firm
 * pays the barrister conventionally (bearing the disbursement) and the
 * barrister CFA is not evaluated at all. A lawFirm-ENGAGED barrister CFA is
 * never mooted — it governs the firm↔barrister leg; whether its fees then
 * flow through the firm's base or are re-charged is decided where the
 * ledger's payments are processed.
 */
export function barristerCfaApplies(
  agreements: PartyAgreements | undefined,
): boolean {
  return (
    agreements?.barristerCfa?.active === true &&
    !(
      agreements.lawFirmCfa?.active === true &&
      agreements.lawFirmCfa.includedDisbursements.includes("barristerFee") &&
      (agreements.barristerCfa.engagedBy ?? "party") !== "lawFirm"
    )
  );
}

export type ConditionalFeeAgreement = {
  active: boolean;
  successFee: SuccessFee;
  // if set the law firm receives a discounted fee even in case of a loss
  lowFee?: { discountPercentage: number };
  // while atypical, it is sometimes possible to recover the success fee
  successFeeRecoveryPercentage?: number;
  /**
   * Stages NOT covered by this agreement — work at these stages is handled
   * as if the CFA didn't exist (billed conventionally, outside the base).
   * Undefined or empty means the agreement covers all stages.
   */
  excludedStages?: Stage[];
};
